import { Howl } from "howler";
import { type FC, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import i18n from "senf-shared/utils/i18n";
import { cn } from "../../../util/classNameConstructor";
import Button from "../../core/button/Button";
import type { TextToSpeechProps } from "./TextToSpeech.types";

function getDefaultLocaleForLanguage(languageCode: string) {
  const locales = {
    en: "en-US",
    es: "es-ES",
    latam: "es-419",
    fr: "fr-FR",
    de: "de-DE",
    tr: "tr-TR",
    ru: "ru-RU",
    uk: "uk-UA",
    ar: "ar-XA",
    pl: "pl-PL",
    cs: "cs-CZ",
    nl: "nl-NL",
    it: "it-IT",
    ja: "ja-JP",
    pt: "pt-PT",
    sv: "sv-SE",
    no: "nb-NO",
    da: "da-DK",
    fi: "fi-FI",
    el: "el-GR",
    ro: "ro-RO",
    hu: "hu-HU",
    // Add more mappings as needed
  };

  // Return the default locale for the language code, or the language code itself if not mapped
  return locales[languageCode] || languageCode;
}

const TextToSpeech: FC<TextToSpeechProps> = ({ id, text, className }) => {
  const { t } = useTranslation();

  const [playing, setPlaying] = useState(false);
  const [howler, setHowler] = useState<Howl | null>(null);
  const [utterance, setUtterance] = useState<SpeechSynthesisUtterance | null>(
    null,
  );
  const [audioLoaded, setAudioLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  // const wpm = 225;
  // const words = text?.trim().split(/\s+/).length;
  // const time = Math.ceil(words / wpm); // Commented out as they're not currently used
  const [currentLanguage, setCurrentLanguage] = useState(
    getDefaultLocaleForLanguage(i18n.resolvedLanguage),
  );

  const handleTextToSpeech = useCallback(
    async (text) => {
      setLoading(true);
      try {
        const response = await fetch(
          `${import.meta.env.VITE_FIREBASE_CLOUD_FUNCTIONS_URL}/googleTTS`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ text, languageCode: currentLanguage }),
          },
        );
        const data = await response.blob();
        const audio = new Howl({
          src: URL.createObjectURL(data),
          format: ["mp3"],
          html5: true, // Required for Safari mobile
          preload: true,
        });
        setHowler(audio);
        setAudioLoaded(true);
      } catch (error) {
        console.error("Error fetching audio", error);
        if ("speechSynthesis" in window) {
          const utterance = new SpeechSynthesisUtterance();
          utterance.rate = 0.9; // From 0.1 to 10
          utterance.pitch = 1.2; // From 0 to 2
          utterance.text = text;
          utterance.lang = currentLanguage;

          setUtterance(utterance);
          setAudioLoaded(true);
        }
      } finally {
        setLoading(false);
      }
    },
    [currentLanguage],
  );

  useEffect(() => {
    if (!text) return;
    if (playing) {
      if (!audioLoaded) {
        handleTextToSpeech(text);
      }
    }
  }, [text, playing, audioLoaded, handleTextToSpeech]);

  useEffect(() => {
    if (!howler) return undefined;

    if (playing) {
      howler.stop();
      // For Safari, ensure audio is loaded before playing
      howler.once("load", () => {
        howler.play();
      });
      // If already loaded, play immediately
      if (howler.state() === "loaded") {
        howler.play();
      }
    } else {
      howler.pause();
    }
    howler.on("end", () => {
      setPlaying(false);
    });
    return () => {
      howler.pause();
      howler.off("end");
      howler.off("load");
    };
  }, [howler, playing]);

  useEffect(() => {
    if (!utterance) return undefined;
    if (playing) {
      window.speechSynthesis.speak(utterance);
    } else {
      window.speechSynthesis.cancel(); // pause doesn't exist, any other way to pause?
    }
    utterance.onend = () => {
      setPlaying(false);
    };

    return () => {
      utterance.onend = null;
      window.speechSynthesis.cancel();
    };
  }, [utterance, playing]);

  useEffect(() => {
    setUtterance(null);
    setAudioLoaded(false);
    setHowler(null);
  }, []);

  useEffect(() => {
    const handleLanguageChange = () => {
      setCurrentLanguage(getDefaultLocaleForLanguage(i18n.resolvedLanguage));
      setUtterance(null);
      setAudioLoaded(false);
      setHowler(null);
    };

    i18n.on("languageChanged", handleLanguageChange);

    return () => {
      i18n.off("languageChanged", handleLanguageChange);
    };
  }, []);

  return (
    <div className={cn("flex items-center gap-2", className)}>
      {playing ? (
        <Button
          variant="outline"
          className="rounded-full bg-white"
          size="md"
          leadingIcon={loading ? "loading" : "pause"}
          text={t("system:stopReadAloud")}
          onClick={() => {
            setPlaying(false);
          }}
        />
      ) : (
        <Button
          variant="outline"
          className="rounded-full bg-white"
          size="md"
          trackId={id}
          leadingIcon={"audio"}
          text={t("system:readAloud")}
          onClick={() => {
            setPlaying(true);
          }}
        />
      )}

      {/* <Typography
        variant="bodyBg"
        color={theme.colors.palette["grey-900"]}
      >
        ~ {time > 1 ? time : 1} Min
      </Typography> */}
    </div>
  );
};

export default TextToSpeech;
